import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { EDSDataGrid } from '@gehc-eds-react/eds-datagrid';
import { EDSBadge } from '@gehc-eds-react/eds-badge';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSDataGridContainer } from '@gehc-eds-react/eds-datagrid-container';
import { EDSIcon } from '@gehc-eds-react/eds-icon';
import { EDSSearch } from '@gehc-eds-react/eds-search';
import { useAppSelector } from '../../redux/hooks';
import {
    selectSources,
    selectSourcesPagination,
    selectSourcesStatus,
    selectSourcesError,
    selectIsTestingConnection,
} from '../../redux/slices/s3SourcesSlice';
import * as s3Styles from './S3UploadCss';
 
// Maps source connection_status values to EDS badge labels and variants.
const getConnectionStatusBadge = (status) => {
    const key = (status || '').toUpperCase();
 
    if (!key) {
        return { label: 'Not tested', variant: 'accent-gray' };
    }
    if (['PASSED', 'PASS', 'CONNECTED', 'HEALTHY', 'SUCCESS'].includes(key)) {
        return { label: 'Connected', variant: 'success' };
    }
    if (['FAILED', 'FAIL', 'ERROR'].includes(key)) {
        return { label: 'Failed', variant: 'alert-high' };
    }
    if (key.includes('PARTIAL')) {
        return { label: 'Partial', variant: 'alert-medium' };
    }
    // Fallback: show raw status with neutral EDS badge.
    const titleCased = key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ');
    return { label: titleCased, variant: 'accent-gray' };
};
 
// variant="delete" gets the hover treatment; other variants just use the base style.
const IconButton = ({ onClick, title, ariaLabel, variant, children }) => {
    const [isHovered, setIsHovered] = useState(false);
    const hoverStyle = variant === 'delete' && isHovered ? s3Styles.iconBtnDeleteHover : null;
 
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={ariaLabel}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ ...s3Styles.iconBtn, ...hoverStyle }}
        >
            {children}
        </button>
    );
};
 
// The arrow next to the Account ID / Display Name link animates on hover of
// the whole button, so the hover state lives here rather than on the arrow.
const AccountLinkButton = ({ onClick, title, ariaLabel, children, arrow }) => {
    const [isHovered, setIsHovered] = useState(false);
 
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={ariaLabel}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={s3Styles.accountLink}
        >
            {children}
            <span style={{ ...s3Styles.accountArrow, ...(isHovered ? s3Styles.accountArrowHover : null) }}>
                {arrow}
            </span>
        </button>
    );
};
 
const getSortValue = (account, key) => {
    if (key === 'account_display_name') {
        return `${account.account_id || ''} ${account.display_name || ''}`.trim().toLowerCase();
    }
    return (account[key] || '').toString().toLowerCase();
};
 
const buildSearchableText = (account) => [
    account.account_id,
    account.display_name,
    account.source_bucket,
    account.replicate_prefix,
].filter(Boolean).join(' ').toLowerCase();
 
/**
* Right-panel source list: search box, sortable table, per-row Deboard /
* Test Connection actions (including navigating to source details), and a
* pagination footer.
*
* Source data (accounts, pagination, load status, error, in-flight test
* connection state) is read directly from the s3SourcesSlice — the parent
* (CreateS3Upload) only owns `selectedContractId` and the callbacks for
* actions whose resulting UI (modals, navigation) it's responsible for.
*/
const SourceListTable = ({
    selectedContractId,
    onOpenSourceDetails,
    onDeleteAccount,
    onTestConnection,
    onPreviousPage,
    onNextPage
}) => {
    const sourceAccounts = useAppSelector(selectSources);
    const accountsPagination = useAppSelector(selectSourcesPagination);
    const sourcesStatus = useAppSelector(selectSourcesStatus);
    const accountsError = useAppSelector(selectSourcesError);
    const isTestConnectionRunning = useAppSelector(selectIsTestingConnection);
    const isAccountsLoading = sourcesStatus === 'loading';
 
    const [sourceSearchTerm, setSourceSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const scrollRef = useRef(null);
 
    const getSearchRank = useCallback((account, search) => {
        const searchableText = buildSearchableText(account);
        if (searchableText === search) return 0;
        if (searchableText.startsWith(search)) return 1;
        if (searchableText.includes(search)) return 2;
        return 3;
    }, []);
 
    const sortedSourceAccounts = useMemo(() => {
        if (!sortConfig.key) return sourceAccounts;
        return [...sourceAccounts].sort((a, b) => {
            const left = getSortValue(a, sortConfig.key);
            const right = getSortValue(b, sortConfig.key);
            if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
            if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [sourceAccounts, sortConfig]);
 
    const filteredSourceAccounts = useMemo(() => {
        const search = sourceSearchTerm.trim().toLowerCase();
        if (!search) return sortedSourceAccounts;
 
        return [...sortedSourceAccounts]
            .filter((account) => buildSearchableText(account).includes(search))
            .sort((a, b) => {
                const rankDiff = getSearchRank(a, search) - getSearchRank(b, search);
                if (rankDiff !== 0) return rankDiff;
                const left = getSortValue(a, sortConfig.key);
                const right = getSortValue(b, sortConfig.key);
                if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
                if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
    }, [getSearchRank, sortConfig.direction, sortConfig.key, sortedSourceAccounts, sourceSearchTerm]);
 
    useLayoutEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [filteredSourceAccounts, sourceSearchTerm]);
 
    const handleSourceSearchInput = useCallback((event) => {
        setSourceSearchTerm(event.detail?.searchTerm ?? event.detail?.value ?? event.detail ?? '');
    }, []);
 
    const handleSourceSearchClear = useCallback(() => setSourceSearchTerm(''), []);
 
    const sourceGridRows = useMemo(
        () => filteredSourceAccounts.map((account, index) => ({
            ...account,
            row_id: `${account.source_id || account.account_id || 'source'}-${account.source_bucket || 'bucket'}-${index}`
        })),
        [filteredSourceAccounts]
    );
 
    const sourceGridColumns = useMemo(() => ({
        row_id: { displayName: 'Row Id', isPrimary: true, isHidden: true },
        account_id: {
            displayName: 'Account ID',
            renderCellValue: (_value, row) => (
                <span>
                    <AccountLinkButton
                        onClick={() => onOpenSourceDetails(row)}
                        title="Open source details"
                        ariaLabel={`Open source details for ${row.account_id || 'source'}`}
                        arrow="›"
                    >
                        <span style={s3Styles.accountLinkText}>
                            {row.account_id || '--'}
                        </span>
                    </AccountLinkButton>
                </span>
            )
        },
        display_name: {
            displayName: 'Display Name',
            renderCellValue: (value) => (
                <span title={value || '--'} style={s3Styles.cellEllipsisName}>
                    {value || '--'}
                </span>
            )
        },
        source_bucket: {
            displayName: 'Source Bucket',
            renderCellValue: (value) => (
                <span title={value || '--'} style={s3Styles.cellEllipsisBucket}>
                    {value || '--'}
                </span>
            )
        },
        replicate_prefix: {
            displayName: 'Replicate Prefix',
            renderCellValue: (value) => (
                <span title={value || '--'} style={s3Styles.cellEllipsisPrefix}>
                    {value || '--'}
                </span>
            )
        },
        connection_status: {
            displayName: 'Status',
            renderCellValue: (_value, row) => {
                const tone = getConnectionStatusBadge(row.connection_status);
                const badgeSize = tone.variant === 'alert-medium' ? 'medium' : 'small';
                return (
                    <EDSBadge
                        variant={tone.variant}
                        value={tone.label}
                        label=""
                        size={badgeSize}
                    />
                );
            }
        },
        action: {
            displayName: 'Action',
            renderCellValue: (_value, row) => (
                <div style={s3Styles.actionCell}>
                    <EDSButton
                        variant="tertiary"
                        type="button"
                        label="Test connection"
                        onEDSButtonClick={() => onTestConnection(row)}
                        isDisabled={isTestConnectionRunning}
                    />
 
                    <span style={s3Styles.actionIconWrap}>
                        <IconButton
                            onClick={() => onDeleteAccount(row)}
                            title="Deboard account"
                            ariaLabel={`Deboard account ${row.account_id || ''}`.trim()}
                            variant="delete"
                        >
                            <EDSIcon iconName="ico-trash-16" />
                        </IconButton>
                    </span>
                </div>
            )
        }
    }), [isTestConnectionRunning, onDeleteAccount, onOpenSourceDetails, onTestConnection]);
 
    return (
        <div className="u--3/4 fill--1" style={s3Styles.sourcePanel}>
            <div style={s3Styles.sourceHeader}>
                <h4 className="u-mt0 u-mb--">Source List</h4>
                <div style={s3Styles.sourceSearchWrap}>
                    <EDSSearch
                        useSearchButton={false}
                        variant="type-in"
                        addListOption="beginning"
                        searchTerm={sourceSearchTerm}
                        placeholder="Search sources..."
                        onEDSSearchInput={handleSourceSearchInput}
                        onEDSSearchClear={handleSourceSearchClear}
                    />
                </div>
            </div>
 
            {!selectedContractId && (
                <div style={s3Styles.noContract}>
                    No contract selected.
                </div>
            )}
 
            {selectedContractId && (
                <>
                    {isAccountsLoading && (
                        <div style={s3Styles.loadingMessage}>Loading source accounts...</div>
                    )}
 
                    {!isAccountsLoading && accountsError && (
                        <div style={s3Styles.errorMessage}>{accountsError}</div>
                    )}
 
                    {!isAccountsLoading && !accountsError && (
                        <div ref={scrollRef} style={s3Styles.gridWrap}>
                            <EDSDataGridContainer>
                                {sourceGridRows.length > 0 && (
                                    <EDSDataGrid
                                        columnConfig={sourceGridColumns}
                                        rows={sourceGridRows}
                                        gridFeatures={["row-hover", "table-flex-horizontal"]}
                                        rowFormat={{ size: 'compact', style: 'divider' }}
                                        labels={{
                                            rowText: 'rows of',
                                            noRowsFoundText: 'No Rows Found',
                                            noFilterResultsText: 'No results found',
                                            emptyGridMessage: 'No active source accounts found for this contract.',
                                            noFilterResultsSecondaryText: ''
                                        }}
                                        visibleRowCount={Math.max(1, sourceGridRows.length)}
                                        totalRowCount={sourceGridRows.length}
                                        activeRowId=""
                                    />
                                )}
                            </EDSDataGridContainer>
 
                            {filteredSourceAccounts.length === 0 && sourceAccounts.length === 0 && (
                                <div style={s3Styles.gridEmpty}>
                                    No active source accounts found for this contract.
                                </div>
                            )}
 
                            {filteredSourceAccounts.length === 0 && sourceAccounts.length > 0 && sourceSearchTerm.trim() && (
                                <div style={s3Styles.gridEmpty}>
                                    No source accounts match "{sourceSearchTerm.trim()}".
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
 
            <div style={s3Styles.pagination}>
                <div style={s3Styles.paginationInfo}>
                    Page {accountsPagination.page} of {Math.max(accountsPagination.totalPages, 1)} | Total accounts: {accountsPagination.totalCount}
                </div>
                <div style={s3Styles.paginationActions}>
                    <EDSButton
                        variant="secondary"
                        type="button"
                        label="Previous"
                        onEDSButtonClick={onPreviousPage}
                        isDisabled={accountsPagination.page <= 1 || isAccountsLoading}
                    />
                    <EDSButton
                        variant="secondary"
                        type="button"
                        label="Next"
                        onEDSButtonClick={onNextPage}
                        isDisabled={
                            accountsPagination.totalPages === 0 ||
                            accountsPagination.page >= accountsPagination.totalPages ||
                            isAccountsLoading
                        }
                    />
                </div>
            </div>
        </div>
    );
};
 
export default SourceListTable;
 